import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Bell,
  Check,
  CheckCheck,
  CalendarPlus,
  ClipboardCheck,
  UserPlus,
  UserMinus,
  Repeat,
} from "lucide-react";
import {
  getLatestNotifications,
  getNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "../../../services/NotificationService";
import { toast } from "../common/Toast/toast";
import {
  getNotificationFrom,
  getNotificationSource,
  getPriorityLabel,
  getPriorityTone,
  summarizeNotification,
  ATTENDANCE_STATUS,
  getAttendanceStatus,
} from "../../../utils/notificationSource";
import { useNavigate } from "react-router-dom";
import {
  PRIORITY_NOT_SET,
  getPriorityMeta,
  normalizePriority,
} from "../../../utils/eventPriority";
import { playNotificationSound } from "../../../utils/notificationSound";
import { dedupeNotifications, notificationKey } from "../../../utils/notificationDedupe";
import { useIsMobile } from "../../../utils/useIsMobile";
import { pulseNotifications } from "./notificationPulse";
import { useSocket } from "../../../context/SocketContext";
import "./notification-bell.css";

/* Arrival is pushed: lms-api emits `notification_new` on the socket the
   moment a notification is saved (notification.service.js). This slow poll
   is only the safety net for a push that could not reach this tab -- a
   dropped connection, a recipient the server could not map to a socket. */
const FALLBACK_POLL_MS = 5 * 60 * 1000;
// More than this arriving at once is one "N more" strip, not a wall of toasts.
const MAX_ARRIVAL_TOASTS = 3;

const timeAgo = (iso) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

// Icon per notification `type` (see notification.service.js and its trigger
// points) so different kinds of notifications stay distinguishable at a
// glance. The *colour* no longer comes from the type — it comes from the
// notification's priority (see getPriorityMeta), so urgency is what the eye
// picks up first and the icon says what kind of thing it is.
const TYPE_ICON = {
  event_created: CalendarPlus,
  attendance_marked: ClipboardCheck,
  batch_added: UserPlus,
  batch_removed: UserMinus,
};
const getTypeIcon = (type) => {
  if (TYPE_ICON[type]) return TYPE_ICON[type];
  if (String(type || "").startsWith("switch_")) return Repeat;
  return Bell;
};

// Real, DB-backed notifications (batch add/remove, batch-switch request
// accepted/rejected, new events, attendance marked — see
// notification.service.js and its trigger points in batch.service.js /
// batchchangerequest.service.js / event.service.js / attendance.service.js).
// Deliberately a small popover anchored to the bell, not a full slide-over
// panel — this is a separate feature from NotificationPanel (the
// events/upcoming-batches panel), which stays as-is.
const NotificationBell = ({ email, recipientType = "student" }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  /* Read through a ref by announceArrivals. `navigate` is a new function on
     every route change (react-router 6 without a data router), and as a
     dependency it rebuilt the whole refresh chain -- so every page change
     fired an extra unread-count request and restarted the timer. */
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const socket = useSocket()?.socket;
  const rootRef = useRef(null);
  /* The popover is a 380px panel anchored to the right of the bell. On a
     phone that is wider than the screen it is anchored in, so it either
     overhangs the edge or gets clipped by it -- and the full page it
     links to is a better answer on a small screen anyway. Below 768px
     the bell is a link to /notifications and the popover never opens. */
  const isMobile = useIsMobile();
  // null until the first count comes back — that first value is just "what's
  // already sitting there", not a new arrival, so it must never itself
  // trigger the sound. Only a genuine increase over the last known count does.
  const prevUnreadRef = useRef(null);

  // Notifications already raised as a toast this session, so a count that
  // dips and rises again never announces the same one twice.
  const announcedRef = useRef(new Set());
  // And by content: a copy of one already announced has a new id, but it is
  // the same notification to the person reading it.
  const announcedKeysRef = useRef(new Set());

  /* The count says THAT something arrived; this finds out WHAT, so the
     toast can say it -- title, summary, who it is from, how urgent -- and
     open straight onto it. Only as many as arrived are asked for (up to
     five), newest first, so this is one small request per arrival, not a
     re-read of the list.

     Declared above refreshUnreadCount, which calls it: a const is not
     hoisted, and the other order throws on first render. */
  const announceArrivals = useCallback(
    async (arrived) => {
      const latest = await getLatestNotifications(
        email,
        recipientType,
        Math.min(Math.max(arrived, 1), 5),
      );
      const unseen = latest.filter((n) => n?.id && !n.read && !announcedRef.current.has(n.id));
      // Every id is remembered, copies included, so a copy is not picked up
      // as new on the next arrival.
      unseen.forEach((n) => announcedRef.current.add(n.id));
      const fresh = dedupeNotifications(unseen).filter(
        (n) => !announcedKeysRef.current.has(notificationKey(n)),
      );
      fresh.forEach((n) => announcedKeysRef.current.add(notificationKey(n)));

      if (!fresh.length) {
        // Could not see what arrived (the request failed, or it was read in
        // another tab first). Listeners still hear that something changed.
        pulseNotifications("unknown");
        return;
      }

      fresh.forEach((n) => announcedRef.current.add(n.id));
      /* The feedback widget listens for these. It used to be told only
         "unknown", because the count could not say more; now it hears the
         actual types and an attendance notice reaches it by name. */
      new Set(fresh.map((n) => String(n.type || "unknown"))).forEach((type) =>
        pulseNotifications(type),
      );

      /* A chat message is already on screen as the chat's own popup
         (SocketContext); a second strip here would say the same thing
         twice. It still counts in the bell and lists in the panel. */
      const toAnnounce = fresh.filter((n) => String(n.type || "") !== "chat_message");
      if (!toAnnounce.length) return;

      const shown = toAnnounce.slice(0, MAX_ARRIVAL_TOASTS);
      // Oldest first: the stack draws in order, so the newest lands last,
      // where the eye already is.
      [...shown].reverse().forEach((n) => {
        toast.notification({
          id: n.id,
          title: n.title,
          summary: summarizeNotification(n),
          source: getNotificationSource(n).label,
          from: getNotificationFrom(n),
          priorityLabel: getPriorityLabel(n.priority),
          priorityTone: getPriorityTone(n.priority),
          attendanceStatus: getAttendanceStatus(n),
          onOpen: () => navigateRef.current("/notifications", { state: { notificationId: n.id } }),
        });
      });

      // A burst is one extra strip, not a wall of them.
      const rest = toAnnounce.length - shown.length;
      if (rest > 0) {
        toast.notification({
          id: `more-${shown[0].id}`,
          title: `${rest} more new notification${rest === 1 ? "" : "s"}`,
          summary: "Open Notifications to read them all.",
          source: "Notifications",
          onOpen: () => navigateRef.current("/notifications"),
        });
      }
    },
    [email, recipientType],
  );

  const refreshUnreadCount = useCallback(() => {
    if (!email) return;
    getUnreadNotificationCount(email, recipientType).then((count) => {
      if (prevUnreadRef.current !== null && count > prevUnreadRef.current) {
        // Same sound a chat message makes — see utils/notificationSound.
        // It swallows the autoplay rejection browsers raise until the page
        // has been interacted with once.
        playNotificationSound();
        announceArrivals(count - prevUnreadRef.current);
      }
      prevUnreadRef.current = count;
      setUnreadCount(count);
    });
  }, [email, recipientType, announceArrivals]);

  useEffect(() => {
    refreshUnreadCount();
    // The safety-net poll, skipped while the tab is hidden: nobody is
    // looking, and coming back checks at once (below).
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") refreshUnreadCount();
    }, FALLBACK_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshUnreadCount();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshUnreadCount]);

  /* The push, plus one catch-up after an outage: anything saved while the
     connection was down was pushed to nobody, so the count is checked once
     it is back. Only after a real loss, though -- it used to refresh on
     every `connect`, and the first connection (the mount has just asked)
     and SocketContext replacing the socket when the user object changes
     are not gaps: each added a request to every page load. */
  const connectionLostRef = useRef(false);
  useEffect(() => {
    if (!socket) return undefined;
    const onNew = (payload) => {
      // Another inbox of the same person (an admin's "user" notices vs this
      // trainer bell): its count isn't this bell's, so no request for it.
      if (payload?.recipientType && payload.recipientType !== recipientType) return;
      refreshUnreadCount();
    };
    /* Withdrawn: the action it announced was undone (an event deleted, a
       student taken back out of a batch). The count drops -- no sound, it
       only plays when the count goes up -- the open panel loses the rows,
       and the Notifications page and widgets hear of it. */
    const onRemoved = (payload) => {
      if (payload?.recipientType && payload.recipientType !== recipientType) return;
      const ids = new Set((payload?.ids || []).map(String));
      setNotifications((prev) => prev.filter((n) => !ids.has(String(n.id))));
      refreshUnreadCount();
      pulseNotifications("removed", { ids: [...ids] });
    };
    const onDisconnect = (reason) => {
      // Our own disconnect() is a replacement or a sign-out, not an outage.
      if (reason !== "io client disconnect") connectionLostRef.current = true;
    };
    const onConnect = () => {
      if (!connectionLostRef.current) return;
      connectionLostRef.current = false;
      refreshUnreadCount();
    };
    socket.on("notification_new", onNew);
    socket.on("notification_removed", onRemoved);
    socket.on("disconnect", onDisconnect);
    socket.on("connect", onConnect);
    return () => {
      socket.off("notification_new", onNew);
      socket.off("notification_removed", onRemoved);
      socket.off("disconnect", onDisconnect);
      socket.off("connect", onConnect);
    };
  }, [socket, recipientType, refreshUnreadCount]);

  useEffect(() => {
    if (!open || !email) return;
    setLoading(true);
    getNotifications(email, recipientType, 1, 20)
      .then((data) => {
        const list = dedupeNotifications(data?.notifications || []);
        setNotifications(list);
        setUnreadCount(data?.unreadCount || 0);
        /* Opening the panel is the one moment the types are known, so
           the pulse can name them. An unread attendance notice here is
           what tells the widget a session is waiting to be rated. */
        const unreadAttendance = list.find(
          (n) => !n?.read && String(n?.type || '') === 'attendance_marked',
        );
        if (unreadAttendance) pulseNotifications('attendance_marked');
      })
      .finally(() => setLoading(false));
  }, [open, email, recipientType]);

  /* Crossing the breakpoint with it open would leave a panel wider than
     the viewport pinned to a bell that is now a link. */
  useEffect(() => {
    if (isMobile) setOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  const handleMarkRead = async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await markNotificationRead(id);
    } catch (error) {
      refreshUnreadCount();
    }
  };

  const handleMarkAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead(email, recipientType);
    } catch (error) {
      refreshUnreadCount();
    }
  };

  // Clicking a notification takes the user to the Notifications page, which
  // carries the full detail the popover has no room for (the topics taught in
  // a session, the batch it belongs to). It's marked read on the way out, so
  // the trip doesn't leave it sitting unread.
  const handleOpenDetail = (n) => {
    if (!n.read) handleMarkRead(n.id);
    setOpen(false);
    navigate("/notifications", { state: { notificationId: n.id } });
  };

  if (!email) return null;

  return (
    <div className="nb-root" ref={rootRef}>
      <button
        type="button"
        className="lms-icon-btn"
        onClick={() => {
          if (isMobile) {
            navigate("/notifications");
            return;
          }
          setOpen((v) => !v);
        }}
        title="Notifications"
        aria-haspopup={isMobile ? undefined : "dialog"}
        aria-expanded={isMobile ? undefined : open}
      >
        <Bell size={18} />
        {/* Label shows only in the phone's bottom bar; topbar.css hides
            it everywhere else. Three bare icons in a tab bar say nothing
            about what they open. */}
        <span className="smt-tb-label">Alerts</span>

        {/* Was an inline style block pinning it to top:2/right:2 of the
            button. In the bottom bar the button becomes a full-width
            tab, so "2px from the right" put the count out at the edge of
            the screen instead of on the bell. A class lets that one
            position be restated for the bar. */}
        {unreadCount > 0 && (
          <span className="lms-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && !isMobile && (
        <div className="nb-panel">
          <div className="nb-head">
            <strong>Notifications</strong>
            {unreadCount > 0 && (
              <button type="button" className="nb-link" onClick={handleMarkAllRead}>
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
          </div>

          <div className="nb-scroll">
            {loading && <div className="nb-state">Loading…</div>}

            {!loading && !notifications.length && (
              <div className="nb-empty">
                <div className="nb-empty-mark">
                  <Bell size={22} strokeWidth={1.5} />
                </div>
                <div className="nb-empty-title">No notifications yet</div>
                <div className="nb-empty-sub">
                  You'll see updates here as they happen.
                </div>
              </div>
            )}

            {!loading && notifications.map((n) => {
              const TypeIcon = getTypeIcon(n.type);
              const priority = normalizePriority(n.priority);
              const { color: priorityColor, label: priorityLabel } = getPriorityMeta(priority);
              /* Attendance says how THIS student was marked: green for
                 present, red for absent. That is the notification's whole
                 content, so it outranks priority for the row's colour and
                 keeps its tint after the row is read. */
              const status = getAttendanceStatus(n);
              const statusMeta = status ? ATTENDANCE_STATUS[status] : null;
              const rowColor = statusMeta ? statusMeta.color : priorityColor;
              return (
                <div
                  key={n.id}
                  onClick={() => handleOpenDetail(n)}
                  className={`${n.read ? 'nb-item is-read' : 'nb-item'}${status ? ` nb-item--${status}` : ''}`}
                  /* The only inline values left in this panel, and the
                     only ones that should be: one notification's own
                     urgency. Handed over as properties rather than as
                     `background` so the stylesheet's :hover can still
                     out-rank the tint -- see notification-bell.css. */
                  style={{
                    '--nb-prio': statusMeta ? statusMeta.color : (n.read ? `${priorityColor}66` : priorityColor),
                    '--nb-tint': statusMeta ? statusMeta.tint : (n.read ? 'transparent' : `${priorityColor}14`),
                  }}
                >
                  <div
                    className="nb-item-mark"
                    style={{ background: `${rowColor}1f` }}
                  >
                    <TypeIcon size={16} strokeWidth={2} color={rowColor} />
                  </div>

                  <div className="nb-item-body">
                    <div className="nb-item-title">{n.title}</div>
                    {n.message && <div className="nb-item-msg">{n.message}</div>}
                    <div className="nb-item-meta">
                      <span className="nb-when">{timeAgo(n.createdAt)}</span>
                      {statusMeta && (
                        <span className={`nb-status nb-status--${status}`}>{statusMeta.label}</span>
                      )}
                      {priority !== PRIORITY_NOT_SET && (
                        <span
                          className="nb-prio"
                          title={`${priorityLabel} priority`}
                          style={{
                            color: priorityColor,
                            background: `${priorityColor}1f`,
                            border: `1px solid ${priorityColor}55`,
                          }}
                        >
                          {priorityLabel}
                        </span>
                      )}
                    </div>
                  </div>

                  {!n.read && (
                    <Check size={14} className="nb-item-check" color={priorityColor} />
                  )}
                </div>
              );
            })}
          </div>

          <div className="nb-foot">
            <button
              type="button"
              className="nb-link"
              onClick={() => { setOpen(false); navigate("/notifications"); }}
            >
              View all notifications
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default NotificationBell;
